import React, { FC, useEffect, useReducer, useState } from 'react';
import { Grid, List } from '@mui/material';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import accessTokenAtom from 'data/atoms/accessTokenAtom';
import { t } from 'i18next';
import { ModuleActionButton } from './ModuleActionButton';
import { ICompanyViewModel, ThirdPartEnum } from 'Interfaces/ICompaniesConfig';
import { useSnackbar } from 'notistack';
import {
  cloudFactoryInitialState,
  cloudFactoryReducer,
  ShowRightSideEnum,
} from './reducer/cloudFactoryReducer';
import { CloudFactoryService } from './APi/CloudFactoryAPI';

import TokenRoundedIcon from '@mui/icons-material/TokenRounded';
import DomainAddRoundedIcon from '@mui/icons-material/DomainAddRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import { CloudFactoryCompanyDrawer } from './drawers/CloudFactoryCompanyDrawer';
import { useGet } from 'http/useApi';
import { rq } from 'http/apiRoutes';
import { CloudFactoryEmployeeDrawer } from './drawers/CloudFactoryEmployeeDrawer';
import { CloudFactoryResourceDrawer } from './drawers/CloudFactoryResourceDrawer';
import { TokenModuleButton } from './TokenModuleButton';
import { CloudFactoryProductDrawer } from './drawers/CloudFactoryProductDrawer';
import { CloudFactoryAllProductsDrawer } from './drawers/CloudFactoryAllProductDrawer';

const viewNames: Record<ShowRightSideEnum, string> = {
  [ShowRightSideEnum.companies]: 'getCompany',
  [ShowRightSideEnum.allProducts]: 'allProducts',
  [ShowRightSideEnum.products]: 'product',
  [ShowRightSideEnum.employees]: 'employee',
  [ShowRightSideEnum.resources]: 'resources',
  [ShowRightSideEnum.employee]: 'employee',
  [ShowRightSideEnum.resource]: 'resource',
};

export const CloudFactory: FC = (): JSX.Element => {
  const [state, dispatch] = useReducer(
    cloudFactoryReducer,
    cloudFactoryInitialState
  );
  const [accessToken, setAccessToken] = useAtom(accessTokenAtom);
  const [loading, setLoading] = useState<boolean>(false);
  const refreshToken = 'ozAPKTeYhy7KW1JJnZbFh0W90glf1mV1i-L-xjSjtBgg4';
  const { height } = useWindowsDimension();
  const { enqueueSnackbar } = useSnackbar();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] =
    useState<ICompanyViewModel | null>(null);

  const { data } = useGet(
    rq.getCompanyByThirdParty(
      ThirdPartEnum.CloudFactory,
      state.isReloadCompanies
    )
  );

  useEffect(() => {
    if (data?.companies) {
      dispatch({ type: 'SET_IS_RELOAD_COMPANIES', payload: false });
      dispatch({ type: 'SET_BLUU_COMPANIES', payload: data.companies });
    }
  }, [data]);

  useEffect(() => {
    if (state.selectedCompany) {
      console.log('Selected company changed:', state.selectedCompany);
    }
  }, [state.selectedCompany]);

  const handleCompanyChange = (name: string) => {
    if (name === 'All') {
      setSelectedCompany(null);
    } else {
      const found = state.bluuCompanies.find((c) => c.name === name);
      setSelectedCompany(found || null);
      // clear all state related to previous company
    }
  };

  const handleRefreshToken = async () => {
    setLoading(true);
    try {
      const tokenResp = await CloudFactoryService.exchangeToken(refreshToken);
      setAccessToken(tokenResp.data.access_token);
      enqueueSnackbar(t('modules.cloudFactory.tokenSuccess'), {
        variant: 'success',
      });
      dispatch({ type: 'SET_IS_RELOAD_COMPANIES', payload: true });
    } catch {
      enqueueSnackbar(t('modules.cloudFactory.tokenError'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = async (moduleId: ShowRightSideEnum) => {
    setLoading(true);
    try {
      setDrawerOpen(true);
      switch (moduleId) {
        case ShowRightSideEnum.companies:
          dispatch({ type: 'SET_IS_RELOAD_COMPANIES', payload: true });
          break;
        case ShowRightSideEnum.allProducts:
          // No specific action needed for all products
          break;
        case ShowRightSideEnum.products:
          // clear previous selected Products
          dispatch({ type: 'CLEAR_SELECTED_COMPANY_RELATED_STATE' });
          break;
        case ShowRightSideEnum.employees:
          // clear previous selected Employees
          dispatch({ type: 'CLEAR_SELECTED_COMPANY_RELATED_STATE' });
          break;
        case ShowRightSideEnum.resources:
          // cl ear previous selected Resources
          dispatch({ type: 'CLEAR_SELECTED_COMPANY_RELATED_STATE' });
          break;
        default:
          break;
      }
      dispatch({ type: 'SET_RIGHT_SIDE_VIEW', payload: moduleId });
    } catch {
      const key = viewNames[moduleId] || 'modules';
      enqueueSnackbar(t(`modules.cloudFactory.${key}Error`), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = (company: ICompanyViewModel | null) => {
    dispatch({ type: 'SET_SELECTED_COMPANY', payload: company });
    setSelectedCompany(company);
    dispatch({ type: 'SET_IS_RELOAD_COMPANIES', payload: true });
    setDrawerOpen(false);
  };

  const noCompany = !selectedCompany;

  return (
    <Grid
      container
      direction='row'
      flexGrow={1}
      height='100%'
      overflow='hidden'
    >
      <Grid item xs={12}>
        <List style={{ height, overflow: 'auto' }}>
          {/* Token-knapp: farge styres internt via tokenValid */}
          <TokenModuleButton
            label={t('modules.cloudFactory.refreshToken')}
            infoTitle={t('modules.cloudFactory.refreshToken')}
            infoText={t('modules.cloudFactory.tokenInfo')}
            loading={loading}
            tokenValid={Boolean(accessToken)}
            onClick={handleRefreshToken}
            icon={<TokenRoundedIcon sx={{ fontSize: 36 }} />}
          />

          {/* Øvrige moduler */}
          <ModuleActionButton
            label={t('modules.cloudFactory.getCompany')}
            infoTitle={t('modules.cloudFactory.getCompany')}
            infoText={t('modules.cloudFactory.companyInfo')}
            disabled={loading || !accessToken}
            loading={loading}
            onClick={() => handleModuleClick(ShowRightSideEnum.companies)}
            icon={<DomainAddRoundedIcon sx={{ fontSize: 36 }} />}
            companySelectOptions={state.bluuCompanies}
            selectedCompanyName={selectedCompany?.name || 'All'}
            onCompanyChange={handleCompanyChange}
            showCompanyName={false}
          />

          <ModuleActionButton
            label={t('modules.cloudFactory.product')}
            infoTitle={t('modules.cloudFactory.product')}
            infoText={t('modules.cloudFactory.productInfo')}
            disabled={loading || !accessToken || noCompany}
            loading={loading}
            onClick={() => handleModuleClick(ShowRightSideEnum.products)}
            icon={<InventoryRoundedIcon sx={{ fontSize: 36 }} />}
            selectedCompanyName={selectedCompany?.name || ''}
            showCompanyName={true}
          />

          <ModuleActionButton
            label={t('modules.cloudFactory.employee')}
            infoTitle={t('modules.cloudFactory.employee')}
            infoText={t('modules.cloudFactory.employeeInfo')}
            disabled={loading || !accessToken || noCompany}
            loading={loading}
            onClick={() => handleModuleClick(ShowRightSideEnum.employees)}
            icon={<PersonRoundedIcon sx={{ fontSize: 36 }} />}
            selectedCompanyName={selectedCompany?.name || ''}
            showCompanyName={true}
          />

          <ModuleActionButton
            label={t('modules.cloudFactory.resources')}
            infoTitle={t('modules.cloudFactory.resources')}
            infoText={t('modules.cloudFactory.resourcesInfo')}
            disabled={loading || !accessToken || noCompany}
            loading={loading}
            onClick={() => handleModuleClick(ShowRightSideEnum.resources)}
            icon={<PersonRoundedIcon sx={{ fontSize: 36 }} />}
            selectedCompanyName={selectedCompany?.name || ''}
            showCompanyName={true}
          />

          <ModuleActionButton
            label={t('modules.cloudFactory.allProducts')}
            infoTitle={t('modules.cloudFactory.allProducts')}
            infoText={t('modules.cloudFactory.allProductsInfo')}
            disabled={loading || !accessToken || !noCompany}
            loading={loading}
            onClick={() => handleModuleClick(ShowRightSideEnum.allProducts)}
            icon={<CategoryRoundedIcon sx={{ fontSize: 36 }} />}
            selectedCompanyName={selectedCompany?.name || ''}
            showCompanyName={false}
          />
        </List>
      </Grid>

      {/* Drawers */}
      <CloudFactoryCompanyDrawer
        open={drawerOpen && state.rightSideView === ShowRightSideEnum.companies}
        onClose={() => setDrawerOpen(false)}
        onSelectCompany={handleSelectCompany}
      />

      {selectedCompany &&
        state.rightSideView === ShowRightSideEnum.products && (
          <CloudFactoryProductDrawer
            open={
              drawerOpen && state.rightSideView === ShowRightSideEnum.products
            }
            company={selectedCompany}
            onClose={() => setDrawerOpen(false)}
          />
        )}

      {selectedCompany &&
        state.rightSideView === ShowRightSideEnum.employees && (
          <CloudFactoryEmployeeDrawer
            open={
              drawerOpen && state.rightSideView === ShowRightSideEnum.employees
            }
            company={selectedCompany}
            onClose={() => setDrawerOpen(false)}
          />
        )}

      {selectedCompany &&
        state.rightSideView === ShowRightSideEnum.resources && (
          <CloudFactoryResourceDrawer
            open={
              drawerOpen && state.rightSideView === ShowRightSideEnum.resources
            }
            company={selectedCompany}
            onClose={() => setDrawerOpen(false)}
          />
        )}

      {!selectedCompany &&
        state.rightSideView === ShowRightSideEnum.allProducts && (
          <CloudFactoryAllProductsDrawer
            open={
              drawerOpen &&
              state.rightSideView === ShowRightSideEnum.allProducts
            }
            onClose={() => setDrawerOpen(false)}
          />
        )}
    </Grid>
  );
};
